import { type StyleConfigProps, type VisibilityElementProps } from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type ElementHorizontalAlignment,
  type FontOptions,
  type NavMenu,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputFontStyleOptions,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { type SelectOption, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

export type NavMenuTemplate = 'default' | 'header';
export type NavMenuItemSize = 'hug' | 'fill' | 'fixed';
export type NavMenuIconPosition = 'left' | 'top';
export type NavMenuDirection = 'horizontal' | 'vertical';

export const NAV_MENU_TEMPLATE_OPTIONS: SelectOption<NavMenuTemplate, string>[] = [
  { label: 'Default', value: 'default' },
  { label: 'Header', value: 'header' },
];

export const NAV_MENU_ITEM_SIZE_OPTIONS: SelectOption<NavMenuItemSize>[] = [
  { value: 'hug', label: 'Fit' },
  { value: 'fill', label: 'Full' },
  { value: 'fixed', label: 'Fixed' },
];

export interface MenuRenderingContext {
  currentPageId: string;
  visibleDropdownFor: NavMenu.RenderItem | undefined;
  useCollapsedMobileMenu: boolean;
  isVertical: boolean;
}

type PropsModel = StyleConfigProps & VisibilityElementProps;
export type NavMenuPropsModel = PropsModel & {
  /** @deprecated */
  version: number;
  content: {
    template: NavMenuTemplate | undefined;
    itemSize: NavMenuItemSize;
    itemWidth: ValueWithUnitOfMeasure;
    iconPosition: NavMenuIconPosition;
    align: ElementHorizontalAlignment;
    direction: NavMenuDirection;
    /** Sets the direction automatically if the menu is placed inside of header or sidebar */
    autoDirection: boolean;
    /**
     * Items are now copied and flattened to `storage` property.
     * This is kept for backwards compatibility in case of any rollbacks.
     * When testers confirm that everything is working fine, this property can be removed.
     * @deprecated
     */
    items: NavMenu.LegacyPropertiesConfigInputNavMenuItem[] | undefined;
    storage: NavMenu.LocalStorage | undefined;
    /** Items that will be created when a new element is created. */
    initialItems?: NavMenu.InitItem[];
    markSelected: boolean;
    sections: {
      collapsible: boolean;
      showDivider: boolean;
    };
    font: FontOptions;
  };
  style: {
    general: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    labels: {
      align: ElementHorizontalAlignment;
    };
    menuStateTabsView: 'default' | 'hover' | 'active';
    submenuStateTabsView: 'default' | 'hover' | 'active';

    //#region Menu button color
    default: {
      text: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      iconColor?: PropertiesConfigInputColorModel;
    };
    hover: {
      text: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      iconColor?: PropertiesConfigInputColorModel;
    };
    active: {
      text: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      iconColor?: PropertiesConfigInputColorModel;
    };
    outline: {
      borderWidth: ValueWithUnitOfMeasure;
      borderRadius: BorderRadiusValue;
    };
    menu: {
      iconSize?: ValueWithUnitOfMeasure;
    };
    //#endregion

    submenu: {
      //#region Submenu button colors
      default: {
        text: PropertiesConfigInputColorModel;
        background: PropertiesConfigInputColorModel;
        border: PropertiesConfigInputColorModel;
        iconColor?: PropertiesConfigInputColorModel;
      };
      hover: {
        text: PropertiesConfigInputColorModel;
        background: PropertiesConfigInputColorModel;
        border: PropertiesConfigInputColorModel;
        iconColor?: PropertiesConfigInputColorModel;
      };
      active: {
        text: PropertiesConfigInputColorModel;
        background: PropertiesConfigInputColorModel;
        border: PropertiesConfigInputColorModel;
        iconColor?: PropertiesConfigInputColorModel;
      };
      //#endregion
      /** Background for dropdown menu */
      background: PropertiesConfigInputColorModel;
      outline: {
        borderWidth: ValueWithUnitOfMeasure;
        borderRadius: BorderRadiusValue;
      };
      iconSize?: ValueWithUnitOfMeasure;
    };
    sections: {
      label: {
        fontFamily: string;
        fontSize: ValueWithUnitOfMeasure;
        fontStyle: PropertiesConfigInputFontStyleOptions;
        fontWeight: string;
        iconSize: ValueWithUnitOfMeasure;
        iconColor: PropertiesConfigInputColorModel;
        lineHeight: ValueWithUnitOfMeasure;
        text: PropertiesConfigInputColorModel;
      };
      titleMargin: ValueWithUnitOfMeasure;
      sectionMargin: ValueWithUnitOfMeasure;
      divider: {
        line: PropertiesConfigInputColorModel;
        height: ValueWithUnitOfMeasure;
      };
    };
    mobile: {
      header: { align: Exclude<ElementHorizontalAlignment, 'center'> };
      overlay: { background: PropertiesConfigInputColorModel };
      burger: { color: PropertiesConfigInputColorModel };
      enabled: boolean;
      label: string;
    };
  };
};
